import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Avatar,
  VStack,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import { cqAPI } from "../../lib/Auth";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useAppContext } from "../../AppContext";

function MonitoringPODetail() {
  const { prevLocation } = useAppContext();
  const { sessionId } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const secondaryTextColor =
    colorMode === "light" ? theme.colors.gray[600] : theme.colors.gray[400];
  const textColor =
    colorMode === "light" ? theme.colors.lightText : theme.colors.darkText;
  const userMessageBg =
    colorMode === "light" ? theme.colors.blue[100] : theme.colors.blue[700];
  const assistantMessageBg =
    colorMode === "light" ? theme.colors.green[100] : theme.colors.green[700];

  const fetchDetails = async () => {
    try {
      const response = await cqAPI.get(
        `/monitoring/po-session?id=${sessionId}`
      );
      setDetails(response.data);
    } catch (error) {
      console.error("Error fetching PO session details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [sessionId]);

  if (loading) {
    return <Box>Loading...</Box>;
  }

  const formatDate = (date) => {
    return moment(date).format("LLL");
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <Flex direction="column" p="5" overflowY="auto" maxH="100vh">
      <Box className="po-details" w="100%" textAlign="left">
        <Flex justifyContent="space-between" alignItems="center" mb="5">
          <Box>
            {prevLocation === "/monitoring/po-sessions" && (
              <Button onClick={handleBackClick} variant="ghost">
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to PO sessions
              </Button>
            )}
          </Box>
        </Flex>

        {!details?.metadata && "Could not find this PO session"}

        {details?.metadata && (
          <>
            <Flex wrap="wrap" mb={6}>
              <Box p="2" w="200px">
                <Text fontWeight="bold">Date</Text>
                <Text>{formatDate(details.metadata.created_date)}</Text>
              </Box>

              <Box p="2" w="200px">
                <Text fontWeight="bold">Business Name</Text>
                <Text>{details.business_name || "N/A"}</Text>
              </Box>

              <Box p="2" w="200px">
                <Text fontWeight="bold">Number of Queries</Text>
                <Text>{details.metadata.user_queries?.length || 0}</Text>
              </Box>

              <Box p="2" w="200px">
                <Text fontWeight="bold">Overall Quality</Text>
                <Text>{details.metadata.overall_quality ?? "N/A"}</Text>
              </Box>

              <Box p="2" w="300px">
                <Text fontWeight="bold">Response Quality</Text>
                {details.metadata.response_quality?.map((score, i) => (
                  <Text key={i}>
                    {score}:{" "}
                    {details.metadata.response_quality_reason?.[i] ?? "—"}
                  </Text>
                ))}
              </Box>
            </Flex>

            <Box mt="4">
              <Text fontSize="2xl" mb="4" fontWeight="bold">
                Conversation
              </Text>
              {details.chat_history && details.chat_history.length > 0 ? (
                <VStack spacing={4} align="stretch">
                  {details.chat_history.map((message, index) => (
                    <Flex
                      key={index}
                      justifyContent={
                        message.role === "user" ? "flex-start" : "flex-end"
                      }
                      w="100%"
                    >
                      <Flex
                        direction={
                          message.role === "user" ? "row" : "row-reverse"
                        }
                        maxW="70%"
                        align="flex-start"
                      >
                        <Avatar
                          size="sm"
                          name={message.role === "user" ? "User" : "AI"}
                          bg={
                            message.role === "user"
                              ? theme.colors.blue[500]
                              : theme.colors.green[500]
                          }
                          color="white"
                          mr={message.role === "user" ? 3 : 0}
                          ml={message.role === "assistant" ? 3 : 0}
                        />
                        <Box
                          p={4}
                          borderRadius="lg"
                          bg={
                            message.role === "user"
                              ? userMessageBg
                              : assistantMessageBg
                          }
                          boxShadow="sm"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            mb={2}
                            color={secondaryTextColor}
                          >
                            {message.role === "user" ? "User" : "Assistant"}
                          </Text>
                          <Box
                            color={textColor}
                            dangerouslySetInnerHTML={{ __html: message.content }}
                          />
                          {message.timestamp && (
                            <Text
                              fontSize="xs"
                              color={secondaryTextColor}
                              mt={2}
                            >
                              {moment(message.timestamp).format(
                                "MMM D, YYYY h:mm a"
                              )}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    </Flex>
                  ))}
                </VStack>
              ) : (
                <Text color={secondaryTextColor}>
                  No conversation history available
                </Text>
              )}
            </Box>
          </>
        )}
      </Box>
    </Flex>
  );
}

export default MonitoringPODetail;